import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseStudyCard = ({ departmentType, orgSize, useCase }) => {
  const caseStudies = [
    {
      id: 1,
      organization: 'Department of Health',
      type: 'government',
      size: 'large',
      useCase: 'compliance',
      logo: 'Building2',
      employees: 2500,
      location: 'Port Moresby, PNG',
      challenge: 'Manual compliance tracking across 20 provincial health offices resulted in frequent PNG Employment Act violations and K450,000 annual penalty costs.',
      solution: 'Implemented automated compliance monitoring with real-time alerts and centralized document management across all provincial offices.',
      timeline: '3 months implementation',
      results: [
        { metric: 'Compliance Score', before: '62%', after: '98%', improvement: '+36%' },
        { metric: 'Annual Penalties', before: 'K450,000', after: 'K12,000', improvement: '-97%' },
        { metric: 'Processing Time', before: '45 hours/week', after: '8 hours/week', improvement: '-82%' },
        { metric: 'Error Rate', before: '18%', after: '2%', improvement: '-89%' }
      ],
      savings: 'K438,000',
      roi: '425%',
      testimonial: 'The automated compliance system transformed our operations. We eliminated manual tracking errors and reduced penalty costs by 97%.',
      author: 'Xinhua Micah',
      position: 'Security Manager for Komodo Dragon Security Services'
    },
    {
      id: 2,
      organization: 'Education Ministry',
      type: 'government',
      size: 'large',
      useCase: 'payroll',
      logo: 'GraduationCap',
      employees: 3200,
      location: 'Port Moresby, PNG',
      challenge: 'Processing payroll for 3,200 teachers across 15 districts took 12 days per cycle with frequent calculation errors and delayed payments.',
      solution: 'Deployed automated payroll engine with PNG tax calculations, NASFUND integration, and direct bank transfer capabilities.',
      timeline: '4 months implementation',
      results: [
        { metric: 'Processing Time', before: '12 days', after: '2 days', improvement: '-83%' },
        { metric: 'Calculation Errors', before: '156/month', after: '3/month', improvement: '-98%' },
        { metric: 'Payment Delays', before: '35%', after: '0%', improvement: '-100%' },
        { metric: 'Staff Hours', before: '320 hours/month', after: '45 hours/month', improvement: '-86%' }
      ],
      savings: 'K625,000',
      roi: '512%',
      testimonial: 'Payroll processing that took 12 days now completes in 2 days with zero payment delays. Our teachers receive accurate payments on time, every time.',
      author: 'John Waninara',
      position: 'Payroll Manager'
    },
    {
      id: 3,
      organization: 'Eastern Highlands Provincial Administration',
      type: 'provincial',
      size: 'medium',
      useCase: 'workforce',
      logo: 'Users',
      employees: 450,
      location: 'Goroka, PNG',
      challenge: 'Decentralized workforce data across 8 departments prevented accurate headcount reporting and budget forecasting.',
      solution: 'Centralized workforce management system with real-time analytics, leave tracking, and demographic reporting.',
      timeline: '2 months implementation',
      results: [
        { metric: 'Data Accuracy', before: '71%', after: '99%', improvement: '+28%' },
        { metric: 'Report Generation', before: '5 days', after: '15 minutes', improvement: '-99%' },
        { metric: 'Budget Variance', before: '±18%', after: '±3%', improvement: '-83%' },
        { metric: 'Leave Processing', before: '3 days', after: '2 hours', improvement: '-96%' }
      ],
      savings: 'K185,000',
      roi: '380%',
      testimonial: 'We finally have accurate, real-time visibility into our workforce. Budget forecasting improved dramatically and leave management is now seamless.',
      author: 'Grace Konga',
      position: 'HR Director'
    },
    {
      id: 5,
      organization: 'Bank South Pacific',
      type: 'private',
      size: 'large',
      useCase: 'compliance',
      logo: 'Landmark',
      employees: 1800,
      location: 'Port Moresby, PNG',
      challenge: 'Multi-branch compliance tracking across 45 locations required manual consolidation and resulted in inconsistent policy enforcement.',
      solution: 'Enterprise compliance platform with centralized policy management, automated audits, and branch-level dashboards.',
      timeline: '5 months implementation',
      results: [
        { metric: 'Policy Compliance', before: '74%', after: '97%', improvement: '+23%' },
        { metric: 'Audit Preparation', before: '6 weeks', after: '3 days', improvement: '-95%' },
        { metric: 'Violation Incidents', before: '42/quarter', after: '4/quarter', improvement: '-90%' },
        { metric: 'Reporting Time', before: '8 hours', after: '20 minutes', improvement: '-96%' }
      ],
      savings: 'K780,000',
      roi: '625%',
      testimonial: 'Centralized compliance management gave us unprecedented visibility across all branches. Audit preparation time dropped from 6 weeks to 3 days.',
      author: 'Sarah Torato',
      position: 'Head of Compliance'
    },
    {
      id: 6,
      organization: 'Oil Search Limited',
      type: 'private',
      size: 'large',
      useCase: 'workforce',
      logo: 'Droplet',
      employees: 950,
      location: 'Port Moresby, PNG',
      challenge: 'Managing workforce across remote mining sites with limited connectivity resulted in delayed reporting and safety compliance gaps.',
      solution: 'Offline-capable workforce management with mobile access, real-time sync, and safety certification tracking.',
      timeline: '4 months implementation',
      results: [
        { metric: 'Reporting Delays', before: '72 hours', after: 'Real-time', improvement: '-100%' },
        { metric: 'Safety Compliance', before: '81%', after: '99%', improvement: '+18%' },
        { metric: 'Certification Tracking', before: 'Manual', after: 'Automated', improvement: '100%' },
        { metric: 'Admin Overhead', before: 'K125,000/month', after: 'K35,000/month', improvement: '-72%' }
      ],
      savings: 'K1,080,000',
      roi: '715%',
      testimonial: 'Real-time workforce visibility across remote sites transformed our operations. Safety compliance improved to 99% and admin costs dropped 72%.',
      author: 'Michael Kombri',
      position: 'Operations Director'
    }
  ];

  const filteredCaseStudies = caseStudies?.filter((study) => {
    if (departmentType !== 'all' && study?.type !== departmentType) return false;
    if (orgSize !== 'all') {
      if (orgSize === 'small' && study?.employees > 50) return false;
      if (orgSize === 'medium' && (study?.employees <= 50 || study?.employees > 500)) return false;
      if (orgSize === 'large' && study?.employees <= 500) return false;
    }
    if (useCase !== 'all' && study?.useCase !== useCase) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {filteredCaseStudies?.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
          <Icon name="Search" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Case Studies Found</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Try adjusting your filters to see more results.</p>
        </div>
      ) : (
        filteredCaseStudies?.map((study) => (
          <div
            key={study?.id}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 p-6 border-b border-emerald-200 dark:border-emerald-700">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <Icon name={study?.logo} size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{study?.organization}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Icon name="MapPin" size={14} />
                        {study?.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Users" size={14} />
                        {study?.employees?.toLocaleString()} Employees
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        {study?.timeline}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{study?.roi}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">ROI</div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Challenge */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="AlertCircle" size={18} className="text-red-500" />
                  <h4 className="font-semibold text-gray-900 dark:text-white">Challenge</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{study?.challenge}</p>
              </div>

              {/* Solution */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Lightbulb" size={18} className="text-amber-500" />
                  <h4 className="font-semibold text-gray-900 dark:text-white">Solution</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{study?.solution}</p>
              </div>

              {/* Results */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="TrendingUp" size={18} className="text-emerald-500" />
                  <h4 className="font-semibold text-gray-900 dark:text-white">Results</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {study?.results?.map((result, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 border border-gray-200 dark:border-slate-600"
                    >
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{result?.metric}</div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Before: {result?.before}</span>
                        <Icon name="ArrowRight" size={14} className="text-gray-400" />
                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                          After: {result?.after}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        {result?.improvement}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Financial Impact */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Annual Savings</div>
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{study?.savings}</div>
                  </div>
                  <Icon name="PiggyBank" size={32} className="text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>

              {/* Testimonial */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 border-l-4 border-emerald-500">
                <div className="flex items-start gap-3">
                  <Icon name="Quote" size={24} className="text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-3">"{study?.testimonial}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{study?.author}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{study?.position}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-slate-700/50 px-6 py-4 border-t border-gray-200 dark:border-slate-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Icon name="CheckCircle" size={16} className="text-emerald-500" />
                  <span>Verified Case Study</span>
                </div>
                <Button variant="outline" size="sm" iconName="ExternalLink">
                  View Full Case Study
                </Button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CaseStudyCard;